import { Broker } from '../broker';
import * as loan_request from '../loan_request';
import { LoanQuoteStatus } from './LoanQuoteStatus';
import { LoanType } from './LoanType';

export enum AcceptedBy {
  MyPage = 'MyPage',
  AutoAccept = 'AutoAccept',
  ACE = 'ACE',
}

export enum Product {
  UnsecuredLoan = 'UnsecuredLoan',
  CreditCard = 'CreditCard',
  SecuredLoan = 'SecuredLoan',
}

export enum LenderProduct {
  DualOffer = 'Dual Offer',
  Annuity = 'Annuity',
  /** MV-3014: Bank Norwegian Rammekreditt — revolving credit line. */
  CreditLine = 'CreditLine',
}

export type SubLoanQuote = {
  ID: string;
  CreatedAt: string;
  UpdatedAt: string;
  LoanQuoteID: string;
  Duration: number;
  Amount: number;
  RefinanceAmount: number;
  TopupAmount: number;
  TotalPayment: number;
  MonthlyPayment: number;
  StartupFee: number;
  EffectiveRate: number;
  NominalRate: number;
  AdministrationFee: number;
};

/** @note the `loan-quote-presentation` does not 100% align with this schema
 * 20250117 – adding "fix me" where api does not align with this schema
 */

export interface LoanQuote {
  ID: string;

  CreatedAt: string | Date;
  UpdatedAt: string | Date;
  ActivatedAt?: string | Date;
  AcceptedAt?: string | Date;
  AcceptedBy?: AcceptedBy;
  DisbursedAt?: string | Date;

  Product: Product;
  LoanType?: LoanType;
  LenderProduct?: string;
  Broker?: Broker;

  LoanRequestID: string;
  LenderID: string;
  // FIXME not present on LQP
  ApplicationID: string;

  Status: LoanQuoteStatus;

  Amount: number;
  RefinanceAmount?: number;
  TopupAmount?: number;
  CashAmount?: number;
  /** Duration in years */
  Duration: number;
  /** DurationMonths in months of year. Between 0 and 11 */
  DurationMonths?: number;
  TotalPayment?: number;
  MonthlyPayment: number;
  StartupFee: number;
  EffectiveRate: number | null;
  NominalRate: number | null;
  AdministrationFee: number;
  AnnualRate?: number;

  LenderReference?: string;
  LenderStatus?: string;
  IsPlaceholderQuote?: boolean;
  SigningLink?: string;
  RequiresDocumentation?: boolean;
  CreditCardInterestFreeDays?: number;
  LenderMaxPossibleAmount?: number;
  SubLoanQuote?: SubLoanQuote;
}

export interface BankRequest {
  LenderID: string;
  LoanRequests: loan_request.LoanRequest[];
}

export interface BankQuote extends LoanQuote {
  LenderID: string;
  LoanRequest: LoanRequestTag;
  LenderStatusURL?: string;
}

export interface LoanRequestTag {
  LoanRequestID: string;
  CreatedAt: string | Date;
  CreatedBy: string;
  Status: loan_request.LoanRequestStatus | undefined;
  Selected?: boolean;
  LenderStatus?: string;
  Amount?: number;
  Duration?: number;
  LenderReference?: string;
}

export interface LoanQuoteRequest {
  applicationID?: string;
  requestID?: string;
  loanQuoteID?: string;
  loanRequestID?: string;
  data?: Partial<LoanQuoteDataRequest>;
}

export enum LoanQuoteRequirementStatus {
  PENDING = 'pending',
  RECEIVED = 'received',
  RESOLVED = 'resolved',
  SENT = 'sent',
}

export interface LoanQuoteRequirementRequest {
  applicationID?: string;
  loanQuoteID: string;
  requirementID: string;
  status: LoanQuoteRequirementStatus;
  data?: Partial<LoanQuoteDataRequest>;
}

export interface LoanQuoteDataRequest extends LoanQuote {
  MarketCountry: string;
  LoanApplicationID: string;
}

export enum LoanQuoteRequirementAction {
  UPLOAD = 'upload',
  SIGN = 'sign',
  IDENTIFY = 'identify',
}

export enum LoanQuoteRequirementPhase {
  BEFORE_ACCEPTANCE = 'before_acceptance',
  BEFORE_APPROVAL = 'before_approval',
  BEFORE_DISBURSEMENT = 'before_disbursement',
}

export interface LoanQuoteRequirement {
  ID: string;
  LoanRequestID: string;
  LoanQuoteID: string;
  Kind: string;
  Action: LoanQuoteRequirementAction;
  Phase: LoanQuoteRequirementPhase;
  ApplicantNumber: number;
  CreatedAt: string | Date;
  UpdatedAt: string | Date;
  ReceivedAt: string | Date;
  SentAt: string | Date;
  ResolvedAt: string | Date;
}

export interface Lender {
  ID: string;
  LoanRequestID: string;
  Name: string;
  ShortName: string;
  Active: boolean;
  Disabled: boolean;
  OffersCreditCard: boolean;
  Info: Array<unknown>;
  MinimumLoanAmount: number;
  MaximumLoanAmount: number;
  MinimumLoanDuration: number;
  MaximumLoanDuration: number;
  CreditCardInterestRate: number;
  CreditCardInterestFreeDays: number;
  Tier: number;
  CreatedAt: string | Date;
  UpdatedAt: string | Date;
  MarketCountry: string;
}
